import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';
import { helpSections } from './helpContent.js';
import './help.scss';

/**
 * Slide-in help drawer — the `?` affordance's target. Backdrop, Escape-to-
 * close, and a body-scroll lock while open, per maintain-in-app-help-docs.
 * `initialSectionId`/`initialTopicId` (from lookupHelpForPathname) seed which
 * section/topic is shown when the drawer opens; falls back to the first
 * section/topic when there's no route-specific mapping.
 */
export default function HelpDrawer({ show, onClose, initialSectionId, initialTopicId }) {
  const [activeSectionId, setActiveSectionId] = useState(initialSectionId || helpSections[0]?.id);
  const [activeTopicId, setActiveTopicId] = useState(initialTopicId || helpSections[0]?.topics[0]?.id);

  // Re-seed the active section/topic every time the drawer is (re)opened with
  // a new route-based lookup, so opening Help from a different page jumps
  // straight to that page's topic.
  useEffect(() => {
    if (!show) return;
    setActiveSectionId(initialSectionId || helpSections[0]?.id);
    setActiveTopicId(initialTopicId || helpSections[0]?.topics[0]?.id);
  }, [show, initialSectionId, initialTopicId]);

  // Escape-to-close + body-scroll lock while open.
  useEffect(() => {
    if (!show) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [show, onClose]);

  if (!show) return null;

  const activeSection = helpSections.find((s) => s.id === activeSectionId) || helpSections[0];

  const handleSelectSection = (sectionId) => {
    setActiveSectionId(sectionId);
    const section = helpSections.find((s) => s.id === sectionId);
    setActiveTopicId(section?.topics[0]?.id);
  };

  // Rendered into <body> rather than in place. The drawer is mounted from
  // AppNavbar, so in the DOM it lands inside <nav>, where Bootstrap's
  // `.navbar-expand-* .offcanvas { position: static; width: auto !important }`
  // rule captures it as though it were the navbar's own menu — stripping the
  // fixed positioning it relies on and pushing it off-screen. A drawer is not
  // part of the navbar's layout, so it should not live in its subtree at all;
  // a portal also keeps it clear of any ancestor stacking context.
  return createPortal(
    <>
      <div className="help-drawer-backdrop" onClick={onClose} aria-hidden="true"></div>

      <div
        className="help-drawer offcanvas offcanvas-end show"
        role="dialog"
        aria-modal="true"
        aria-label="In-app help"
      >
        <div className="offcanvas-header border-bottom">
          <h5 className="offcanvas-title">
            <i className="fa fa-circle-question me-2" aria-hidden="true"></i>
            Help
          </h5>
          <button
            type="button"
            className="btn-close"
            aria-label="Close help"
            onClick={onClose}
          ></button>
        </div>

        <HelpNav sections={helpSections} activeSectionId={activeSection?.id} onSelectSection={handleSelectSection} />

        <div className="offcanvas-body p-0">
          <HelpContent section={activeSection} activeTopicId={activeTopicId} onSelectTopic={setActiveTopicId} />
        </div>
      </div>
    </>,
    document.body
  );
}
